import { useState } from 'react';
import { useBreakpoint } from '@/hooks/useBreakpoint';
import MobileUploadSheet from './MobileUploadSheet';
import {
  Modal,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Input,
  Tabs,
  Tab,
} from '@heroui/react';
import { UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { useUploadKnowledge, useCreateLink } from '@/api/knowledge';
import { KnowledgeItemType } from '@estimator/shared';
import type { KnowledgeItemTypeEnum } from '@estimator/shared';

interface UploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  folderId?: string;
}

type UploadType = Exclude<KnowledgeItemTypeEnum, KnowledgeItemType.EMAIL>;

const ACCEPT_MAP: Record<UploadType, string> = {
  PDF: '.pdf',
  IMAGE: 'image/*',
  DRAWING: '.pdf,image/*',
  LINK: '',
};

export default function UploadModal({ isOpen, onClose, projectId, folderId }: UploadModalProps) {
  const { isDesktop } = useBreakpoint();
  const [uploadType, setUploadType] = useState<UploadType>(KnowledgeItemType.PDF);
  const [file, setFile] = useState<File | null>(null);
  const [linkTitle, setLinkTitle] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  const uploadMutation = useUploadKnowledge(projectId);
  const createLinkMutation = useCreateLink(projectId);

  const isLink = uploadType === KnowledgeItemType.LINK;

  const resetForm = () => {
    setFile(null);
    setLinkTitle('');
    setLinkUrl('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = () => {
    if (isLink) {
      if (!linkTitle.trim() || !linkUrl.trim()) return;
      createLinkMutation.mutate(
        { title: linkTitle.trim(), url: linkUrl.trim() },
        {
          onSuccess: () => toast.success('Link added'),
          onError: (err) => toast.error(`Failed to add link: ${err.message}`),
        },
      );
    } else {
      if (!file) return;
      const filename = file.name;
      const formData = new FormData();
      formData.append('files', file);
      formData.append('type', uploadType);
      if (folderId) formData.append('folder_id', folderId);

      toast.promise(
        new Promise((resolve, reject) => {
          uploadMutation.mutate(formData, {
            onSuccess: resolve,
            onError: reject,
          });
        }),
        {
          loading: `Uploading ${filename}...`,
          success: `${filename} uploaded`,
          error: (err) => `Upload failed: ${err?.message ?? 'Unknown error'}`,
        },
      );
    }

    // Close modal immediately regardless of mutation outcome
    handleClose();
  };

  const canSubmit = isLink
    ? linkTitle.trim().length > 0 && linkUrl.trim().length > 0
    : file !== null;

  if (!isDesktop) {
    return (
      <MobileUploadSheet
        isOpen={isOpen}
        onClose={onClose}
        projectId={projectId}
        folderId={folderId}
      />
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md">
      <ModalContent style={{ backgroundColor: 'var(--bg-surface)' }}>
        <ModalHeader
          className="text-sm font-semibold"
          style={{ color: 'var(--text-primary)' }}
        >
          Add Knowledge Item
        </ModalHeader>

        <ModalBody className="gap-4">
          {/* Type selector */}
          <Tabs
            selectedKey={uploadType}
            onSelectionChange={(key) => {
              setUploadType(key as UploadType);
              resetForm();
            }}
            variant="bordered"
            size="sm"
            classNames={{
              tabList: 'bg-[var(--bg-panel)] border-[var(--border)]',
              tab: 'text-xs text-[var(--text-muted)] data-[selected=true]:text-[var(--accent)]',
              cursor: 'bg-[var(--bg-hover)]',
            }}
          >
            <Tab key="PDF" title="PDF" />
            <Tab key="IMAGE" title="Image" />
            <Tab key="DRAWING" title="Drawing" />
            <Tab key="LINK" title="Link" />
          </Tabs>

          {isLink ? (
            <>
              <Input
                label="Title"
                size="sm"
                value={linkTitle}
                onValueChange={setLinkTitle}
                placeholder="e.g. Project Specifications"
                classNames={{
                  inputWrapper: 'bg-[var(--bg-panel)] border-[var(--border)]',
                  input: 'text-[var(--text-primary)]',
                  label: 'text-[var(--text-secondary)]',
                }}
              />
              <Input
                label="URL"
                size="sm"
                value={linkUrl}
                onValueChange={setLinkUrl}
                placeholder="https://..."
                type="url"
                classNames={{
                  inputWrapper: 'bg-[var(--bg-panel)] border-[var(--border)]',
                  input: 'text-[var(--text-primary)]',
                  label: 'text-[var(--text-secondary)]',
                }}
              />
            </>
          ) : (
            <div>
              <label
                className="flex flex-col items-center justify-center gap-2 py-8 rounded-lg border-2 border-dashed cursor-pointer transition-colors"
                style={{ borderColor: 'var(--border)', backgroundColor: 'var(--bg-base)' }}
              >
                <UploadCloud size={32} style={{ color: 'var(--text-muted)' }} />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {file ? file.name : 'Click to select a file'}
                </span>
                {file && (
                  <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>
                    {(file.size / 1024).toFixed(1)} KB
                  </span>
                )}
                <input
                  type="file"
                  className="hidden"
                  accept={ACCEPT_MAP[uploadType]}
                  onChange={(e) => setFile(e.target.files?.[0] ?? null)}
                />
              </label>
            </div>
          )}
        </ModalBody>

        <ModalFooter>
          <Button
            size="sm"
            variant="bordered"
            onPress={handleClose}
            className="border-[var(--border)] text-[var(--text-secondary)]"
          >
            Cancel
          </Button>
          <Button
            size="sm"
            color="primary"
            onPress={handleSubmit}
            isDisabled={!canSubmit}
          >
            {isLink ? 'Add Link' : 'Upload'}
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
